/**
 * RouteShapes.jsx — shared route-rendering primitives (b99.9.55).
 *
 * Centralised so the same polyline / via-shaping / hover-ring style is
 * applied in every map across the app. Component-level callers MUST
 * import from here instead of declaring their own `<Polyline>` props.
 *
 * Why a separate file from `mapIcons.js`: `mapIcons.js` is a pure-JS
 * module (Leaflet `L.divIcon` calls). React components live here so
 * the bundler can tree-shake them cleanly without dragging Leaflet
 * imports into non-map pages.
 */
import { Polyline, CircleMarker, Tooltip } from "react-leaflet";
import {
  ROUTE_COLOR,
  POLYLINE_OUTLINE,
  POLYLINE_PRIMARY,
  VIA_SHAPING,
  HOVER_RING,
} from "./mapIcons";

/**
 * `<RoutePolyline coords color={ROUTE_COLOR} />` — renders the dual
 * white-outline + coloured-line pair. `coords` is `[[lat,lng], ...]`.
 * Pass an explicit `color` when a per-segment colour is needed (e.g.
 * multi-segment overview map); defaults to `ROUTE_COLOR` (orange).
 */
export function RoutePolyline({ coords, color = ROUTE_COLOR, primaryProps = {}, outlineProps = {} }) {
  if (!Array.isArray(coords) || coords.length < 2) return null;
  return (
    <>
      <Polyline positions={coords} pathOptions={{ ...POLYLINE_OUTLINE, ...outlineProps }} />
      <Polyline positions={coords} pathOptions={{ color, ...POLYLINE_PRIMARY, ...primaryProps }} />
    </>
  );
}

/**
 * `<ViaShapingMarker position={[lat,lng]} tooltipsEnabled />` — small
 * orange circle that marks a Google `!3m4!1m2` shaping/via point. No
 * label by default; pass `tooltipText` to surface one (the public
 * sheet's hover-highlight uses this).
 */
export function ViaShapingMarker({ position, tooltipText = null, eventHandlers = null }) {
  return (
    <CircleMarker
      center={position}
      pathOptions={VIA_SHAPING}
      eventHandlers={eventHandlers || undefined}
    >
      {tooltipText && (
        <Tooltip direction="top" offset={[0, -8]} opacity={1} permanent={false} sticky>
          {tooltipText}
        </Tooltip>
      )}
    </CircleMarker>
  );
}

/**
 * `<HoverHighlight point label?/>` — blue ring rendered around the
 * point currently hovered in the sidebar. When `label` is provided AND
 * `showTooltip` is true, a tooltip with the same visual style as the
 * permanent map tooltips is emitted. The caller decides whether to
 * show the tooltip — when the underlying marker already has a
 * permanent tooltip, the caller should pass `showTooltip={false}` to
 * avoid duplication (the user's b99.9.55 spec on hover behaviour).
 */
export function HoverHighlight({ point, label = null, showTooltip = false, tooltipOffset = [0, -18] }) {
  if (!point || point.lat == null || point.lng == null) return null;
  return (
    <CircleMarker center={[point.lat, point.lng]} pathOptions={HOVER_RING}>
      {showTooltip && label && (
        <Tooltip direction="top" offset={tooltipOffset} opacity={1} permanent sticky={false}>
          {label}
        </Tooltip>
      )}
    </CircleMarker>
  );
}
